import { Children, ReactNode } from 'react';
import { useUserPreference } from '@/features/user-preferences/user-preferences.hooks';
import { TimelineEmpty, TimelineList } from './TimelineList';

interface TimelineProps {
  /**
   * Timeline entries in newest-first order. Each entry should carry its own
   * `previousTimestamp` (the chronologically older neighbor) so delta-mode
   * rendering stays correct regardless of display direction.
   */
  children?: ReactNode;
  /** Optional message composer rendered above the list (or below when reversed). */
  composer?: ReactNode;
  /** Optional override for the empty-state message. */
  emptyMessage?: string;
}

/**
 * Shared timeline wrapper. Owns the global "reverse timeline order" user
 * preference: when enabled, the list flips to oldest-first and the composer
 * moves to the bottom. Consumers stay unaware of the preference.
 */
export function Timeline({ children, composer, emptyMessage }: TimelineProps) {
  const [reverseOrder] = useUserPreference('reverseTimelineOrder');

  const entries = Children.toArray(children);
  const ordered = reverseOrder ? [...entries].reverse() : entries;

  const list =
    ordered.length > 0 ? (
      <TimelineList>{ordered}</TimelineList>
    ) : (
      <TimelineEmpty message={emptyMessage} />
    );

  if (!composer) return list;

  return reverseOrder ? (
    <>
      {list}
      <div className="mt-6">{composer}</div>
    </>
  ) : (
    <>
      {composer}
      {list}
    </>
  );
}
